// Amenities: food/drink stands, toilets, shops, attractions — cell-based buildings that serve visitor needs.
import * as THREE from 'three';
import { store, pushNews } from '../core/store.js';
import { bus } from '../core/bus.js';
import { AMENITIES } from './amenityCatalog.js';
import { buildAmenityMid, buildAmenityModel, buildingImpostor, FACING_DIRS, FOOTPRINTS, facingAngle } from './amenityModels.js';
import { getBuildingFront, getBuildingSink, setMeasuredBuildingFootprint } from '../world/buildingConfig.js';

export { AMENITIES } from './amenityCatalog.js';
export { AMENITY_THUMBS, buildAmenityMid, buildAmenityModel, generateAmenityThumbnails } from './amenityModels.js';

export class AmenitySystem {
  constructor(world, build) {
    this.world = world;
    this.build = build;
    this.items = [];                 // { type, cx, cz, model }  (cx,cz = anchor cell, min corner)
    this.byCell = new Map();         // "x,z" -> item (every footprint cell)
    this.group = new THREE.Group();
    world.renderer.scene.add(this.group);
    this._subs = [
      bus.on('build:amenityPlace', ({ type, cx, cz }) => this.tryPlace(type, cx, cz)),
      bus.on('overlay:vet', (v) => { for (const it of this.items) { if (it.ring) it.ring.visible = v; if (it.ringFill) it.ringFill.visible = v; if (it.sweep) it.sweep.visible = v; } }),
    ];
    this._computeFootprints();
  }

  dispose() {
    this._subs?.forEach((u) => u());
    this.world.renderer.scene.remove(this.group);
    this.items.length = 0;
    this.byCell.clear();
  }

  // Measure an accurate grid footprint per type from the real mesh (world-space corners).
  _computeFootprints() {
    const cs = this.world.terrain.cellSize;
    const v = new THREE.Vector3();
    for (const type of Object.keys(AMENITIES)) {
      if (type === 'entry') continue;
      let fp = AMENITIES[type].fp || [1, 1];
      try {
        const m = buildAmenityModel(AMENITIES[type], cs, type);
        m.updateMatrixWorld(true);
        let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity, any = false;
        m.traverse((o) => {
          if (!o.isMesh || !o.geometry) return;
          if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
          const bb = o.geometry.boundingBox;
          for (const ax of [bb.min.x, bb.max.x]) for (const ay of [bb.min.y, bb.max.y]) for (const az of [bb.min.z, bb.max.z]) {
            v.set(ax, ay, az).applyMatrix4(o.matrixWorld);
            minX = Math.min(minX, v.x); maxX = Math.max(maxX, v.x);
            minZ = Math.min(minZ, v.z); maxZ = Math.max(maxZ, v.z);
            any = true;
          }
        });
        if (any) fp = [Math.max(1, Math.ceil((maxX - minX) / cs)), Math.max(1, Math.ceil((maxZ - minZ) / cs))];
      } catch (e) { /* keep default */ }
      FOOTPRINTS[type] = fp;
      setMeasuredBuildingFootprint(type, fp);
    }
  }

  // All grid cells covered by a footprint anchored at (cx, cz).
  _footprintCells(type, cx, cz) {
    const [fw, fd] = FOOTPRINTS[type] || AMENITIES[type].fp || [1, 1];
    const cells = [];
    for (let dz = 0; dz < fd; dz++) for (let dx = 0; dx < fw; dx++) cells.push([cx + dx, cz + dz]);
    return cells;
  }

  tryPlace(type, cx, cz) {
    const def = AMENITIES[type];
    const t = this.world.terrain;
    const cells = this._footprintCells(type, cx, cz);
    const bigEnc = t.cells * t.cells * 0.4;
    for (const [x, z] of cells) {
      if (x < 0 || z < 0 || x >= t.cells || z >= t.cells) { bus.emit('ui:toast', { text: 'Out of bounds.', kind: 'error' }); return; }
      const key = `${x},${z}`;
      if (this.byCell.has(key) || this.build.paths.has(key)) { bus.emit('ui:toast', { text: 'Space is occupied.', kind: 'error' }); return; }
      if (this.build.enclosures.some((e) => e.cells.has(key) && e.cells.size < bigEnc)) { bus.emit('ui:toast', { text: 'Cannot build inside an enclosure.', kind: 'error' }); return; }
      if (t.cellSlope(x, z) > 0.7) { bus.emit('ui:toast', { text: 'Too steep here.', kind: 'error' }); return; }
    }
    const nearPath = cells.some(([x, z]) => [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dx, dz]) => this.build.paths.has(`${x + dx},${z + dz}`)));
    if (!nearPath) { bus.emit('ui:toast', { text: 'Must be placed next to a path.', kind: 'error' }); return; }
    const s = store.get();
    if (s.money < def.price) { bus.emit('ui:toast', { text: 'Not enough cash!', kind: 'error' }); return; }
    store.set({ money: s.money - def.price });
    this._add(type, cx, cz);
    pushNews(`${def.label} opened for business.`, 'info');
  }

  _add(type, cx, cz) {
    const def = AMENITIES[type];
    const t = this.world.terrain, half = t.worldSize / 2;
    const [fw, fd] = FOOTPRINTS[type] || def.fp || [1, 1];
    const model = buildAmenityModel(def, t.cellSize, type);
    const wx = (cx + fw / 2) * t.cellSize - half;
    const wz = (cz + fd / 2) * t.cellSize - half;
    const sink = getBuildingSink(type);
    model.position.set(wx, t.heightAtWorld(wx, wz) - sink, wz);
    const item = { type, cx, cz, model, facing: 0 };
    this.group.add(model);
    this.items.push(item);
    this._footprintCells(type, cx, cz).forEach(([x, z]) => this.byCell.set(`${x},${z}`, item));
    // Remove any baked trees standing where the building goes.
    if (window.__clearVegAt) {
      const cs = t.cellSize, half = t.worldSize / 2;
      window.__clearVegAt(cx * cs - half, (cx + fw) * cs - half, cz * cs - half, (cz + fd) * cs - half);
    }
    this._orientToPath(item);
  }

  _basePosition(item) {
    const t = this.world.terrain, half = t.worldSize / 2;
    const [fw, fd] = FOOTPRINTS[item.type] || AMENITIES[item.type].fp || [1, 1];
    const wx = (item.cx + fw / 2) * t.cellSize - half;
    const wz = (item.cz + fd / 2) * t.cellSize - half;
    return new THREE.Vector3(wx, t.heightAtWorld(wx, wz) - getBuildingSink(item.type), wz);
  }

  // Pick the side with the strongest path contact along the full footprint edge.
  // Golden Age saves do not store facing, so this is the compatibility path for old scenarios.
  _pathFacing(item) {
    const [fw, fd] = FOOTPRINTS[item.type] || AMENITIES[item.type].fp || [1, 1];
    const scores = { 0: 0, 1: 0, 2: 0, 3: 0 };
    for (let x = item.cx; x < item.cx + fw; x++) {
      if (this.build.paths.has(`${x},${item.cz + fd}`)) scores[0]++;
      if (this.build.paths.has(`${x},${item.cz - 1}`)) scores[2]++;
    }
    for (let z = item.cz; z < item.cz + fd; z++) {
      if (this.build.paths.has(`${item.cx + fw},${z}`)) scores[1]++;
      if (this.build.paths.has(`${item.cx - 1},${z}`)) scores[3]++;
    }
    const order = [0, 1, 2, 3];
    let best = null;
    for (const facing of order) {
      if (scores[facing] <= 0) continue;
      if (best == null || scores[facing] > scores[best]) best = facing;
    }
    return best;
  }

  _alignFacingToFootprint(item) {
    const dir = FACING_DIRS[item.facing];
    if (!dir) return;
    const t = this.world.terrain, half = t.worldSize / 2, cs = t.cellSize;
    const [fw, fd] = FOOTPRINTS[item.type] || AMENITIES[item.type].fp || [1, 1];
    item.model.updateMatrixWorld(true);
    const bb = new THREE.Box3().setFromObject(item.model);
    if (dir.x === 1) item.model.position.x += ((item.cx + fw) * cs - half) - bb.max.x;
    else if (dir.x === -1) item.model.position.x += (item.cx * cs - half) - bb.min.x;
    else if (dir.z === 1) item.model.position.z += ((item.cz + fd) * cs - half) - bb.max.z;
    else if (dir.z === -1) item.model.position.z += (item.cz * cs - half) - bb.min.z;
  }

  // Rotate the building so its authored front door faces the selected world side.
  _setFacing(item, facing) {
    if (facing == null) return;
    item.facing = facing;
    item.model.position.copy(this._basePosition(item));
    item.model.rotation.y = facingAngle(facing) - facingAngle(getBuildingFront(item.type));
    this._alignFacingToFootprint(item);
  }

  _orientToPath(item) {
    this._setFacing(item, this._pathFacing(item));
  }

  // Apply the stored facing (0..3 quarter-turns) + refresh the front marker.
  _applyFacing(item) {
    this._setFacing(item, item.facing ?? this._pathFacing(item));
  }

  // Bulldoze support: remove the amenity covering this cell, return the refund.
  removeAt(cx, cz) {
    const item = this.byCell.get(`${cx},${cz}`);
    if (!item) return 0;
    this.group.remove(item.model);
    if (item.block) this.group.remove(item.block);
    if (item.mid) this.group.remove(item.mid);
    if (item.floor) this.group.remove(item.floor);
    this.items.splice(this.items.indexOf(item), 1);
    this._footprintCells(item.type, item.cx, item.cz).forEach(([x, z]) => this.byCell.delete(`${x},${z}`));
    pushNews(`${AMENITIES[item.type].label} demolished.`, 'info');
    return AMENITIES[item.type].price * 0.4;
  }

  // Distance LOD for buildings: far-but-in-view ones swap to a cheap block (off-screen ones are
  // already frustum-culled for free). Throttled; block proxy is built lazily and cached per item.
  updateLOD(cam) {
    this._lodF = (this._lodF || 0) + 1;
    if (this._lodF % 20 !== 0) return;
    const MID = 95, FAR = 200;
    for (const it of this.items) {
      if (!it.model) continue;
      if (it.block === undefined) {
        const bb = new THREE.Box3().setFromObject(it.model);
        const ctr = bb.getCenter(new THREE.Vector3());
        // Far LOD = camera-facing billboard of the mid model (proportions approximate — that's fine).
        const imp = buildingImpostor(it.type, this.world.terrain.cellSize);
        const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: imp.tex, transparent: true, alphaTest: 0.5, depthWrite: true }));
        sp.scale.set(imp.md, imp.md, 1);
        sp.position.copy(ctr); sp.visible = false;
        this.group.add(sp); it.block = sp;
        try {
          const mid = buildAmenityMid(AMENITIES[it.type], this.world.terrain.cellSize, it.type);
          mid.position.copy(it.model.position); mid.rotation.copy(it.model.rotation); mid.scale.set(1, 1, 1);
          mid.visible = false; mid.castShadow = false;
          this.group.add(mid); it.mid = mid;
        } catch (e) { it.mid = null; }
      }
      const d = cam.position.distanceTo(it.model.position);
      const tier = d > FAR ? 'blk' : d > MID ? 'mid' : 'hi';
      it.model.visible = tier === 'hi';
      if (it.floor) it.floor.visible = tier !== 'blk';
      if (it.mid) it.mid.visible = tier === 'mid';
      it.block.visible = tier === 'blk' || (tier === 'mid' && !it.mid);
    }
  }

  // amenity adjacent to a given path cell serving a specific need
  findAdjacent(cx, cz, need) {
    // Buildings are far bigger than the base map assumed, so their service reaches ~4× further:
    // search outward rings (nearest first) up to radius 12 instead of only the touching cells.
    for (let r = 1; r <= 12; r++) {
      for (let dx = -r; dx <= r; dx++) for (let dz = -r; dz <= r; dz++) {
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue;
        const item = this.byCell.get(`${cx + dx},${cz + dz}`);
        if (item && AMENITIES[item.type] && AMENITIES[item.type].serves === need) return item;
      }
    }
    return null;
  }

  // Restore from a save: place without cost/validation/news, then apply per-building overrides.
  restore(type, cx, cz, opts) {
    if (!AMENITIES[type]) return;
    this._add(type, cx, cz);
    if (opts) {
      const it = this.items[this.items.length - 1];
      if (it) {
        if (opts.facing != null) { it.facing = opts.facing; this._applyFacing(it); }
        if (opts.tier != null) it.tier = opts.tier;
        if (opts.staff != null) it.staff = opts.staff;
        if (opts.closed) it.closed = true;
        if (opts.name) it.name = opts.name;
      }
    }
  }
}
